// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FloatingTooltipStyles } from "@sc/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@sc/App/Components/Header/HeaderClickable";
import FloatingMenuTooltip from "@sc/App/Components/Menu/FloatingMenuTooltip";
import MiniCart from "@sc/App/Components/MiniCart/MiniCart";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { xIsAuthenticated } from "@sc/App/Store/Selectors/GeneralSelectors";
import Cart from "@sc/XDL/Icons/Cart";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    isAuthenticated: xIsAuthenticated(state),
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-CHANGES
    tippyStyles?: FloatingTooltipStyles;
    productsWrapper?: InjectableCss;
    bottomSectionWrapper?: InjectableCss;
    miniCartComponent?: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    // XNG-Change
    isTabletScreen,
}) => {
    // XNG-Change
    const [isPopupShown, setIsPopupShown] = React.useState(false);
    const [visible, setVisible] = useState(false);
    const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);
    // XNG-Change
    const history = useHistory();

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    // XNG-Change
    const openCartPageHandler = () => {
        if (!cartUrl) {
            return;
        }
        history.push(cartUrl);
    };

    const handleClose = () => {
        setVisible(false);
        setIsPopupShown(false);
    };

    // XNG-Change: Handle hover to open, but don't close on hover out
    const handleMouseEnter = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        if (!visible) {
            setVisible(true);
            setIsPopupShown(true);
        }
    };

    // XNG-Change
    const headerClickable = (
        <HeaderClickable
            onClick={openCartPageHandler}
            iconProps={{
                src: selectedIcon === "cart" ? Cart : icon,
                css: css`
                    &,
                    svg {
                        width: 18px;
                    }
                `,
            }}
            data-test-selector="cartLink"
            title={translate("Cart")}
            cartLines={retainedTotalCountToDisplay}
            isOpen={isPopupShown}
            {...(!isTabletScreen && {
                onMouseEnter: handleMouseEnter,
            })}
            extendedStyles={styles.cartLink}
        />
    );

    if (isTabletScreen) {
        return headerClickable;
    }

    // if (isCompactHeaderCart) {
    //     return (
    //         <Link
    //             {...routerLink}
    //             color={theme.header.linkColor}
    //             href={cartUrl}
    //             icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
    //             data-test-selector="cartLink"
    //         >
    //             <Typography as="span" {...styles.compactCountText}>
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //             <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
    //         </Link>
    //     );
    // }

    return (
        <FloatingMenuTooltip
            triggerComponent={headerClickable}
            isOpen={visible}
            onOpenChange={open => {
                setVisible(open);
                setIsPopupShown(open);
            }}
            trigger="manual"
            placement="left-end"
            overlay={true}
            drawer={true}
            showArrow={false}
            offset={0}
            extendedStyles={styles.tippyStyles}
            drawerTitle="Cart"
        >
            <MiniCart onClose={handleClose} isPopupShown={isPopupShown} />
        </FloatingMenuTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
